import { useEffect, useState } from 'react';

import { EColorVariablesV2, EPaletteModes, TColorVariantKeys, ThemePack } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Modal from '@ui/modal/modal';
import styles from './theme-pack-save-modal.module.css';
import Checkbox from '@ui/checkbox/checkbox';
import { useAppSelector } from '@/store/hooks';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import createScheduler from '@/polyfills/create-scheduler';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import isGlobalThemePack from '@/utils/is-global-theme-pack';

type Props = {
  open: boolean;
  close: () => void;
  onSave: (name: string, asNew: boolean) => void;
  activeThemePack: ThemePack;
  activeThemeMode: EPaletteModes;
  saving: boolean;
  editingNonSelectedTheme?: boolean;
  /** force every save to fork a new theme pack instead of overwriting the shared one in
   * place - for contexts (like campaign editor) that should never mutate a theme pack
   * used elsewhere in the project */
  forceSaveAsNew?: boolean;
};

const COLOR_VARIABLES_LIST_V2: { name: TColorVariantKeys; description: string }[] = [
  { name: 'accentColor', description: 'Primary' },
  { name: 'secondaryAccentColor', description: 'Accent' },
  { name: 'secondaryBackgroundColor', description: 'Banner' },
  { name: 'backgroundColor', description: 'Background Color' },
  { name: 'buttonTextColor', description: 'Button Text' },
  { name: 'headingTextColor', description: 'Heading Text' },
  { name: 'bodyTextColor', description: 'Description Text' },
];

export default function ThemePackSaveModal({
  open,
  onSave,
  close,
  activeThemePack,
  activeThemeMode,
  saving,
  editingNonSelectedTheme = false,
  forceSaveAsNew = false,
}: Props) {
  const allThemes = useAppSelector(selectThemePacks);

  const currentColors = activeThemePack.colors[activeThemeMode];

  const isGlobal = isGlobalThemePack(activeThemePack);
  const lockedToNew = isGlobal || forceSaveAsNew;

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [saveAsNew, setSaveAsNew] = useState(lockedToNew);

  useEffect(() => {
    if (open) {
      setName(activeThemePack.title);
      setSaveAsNew(isGlobalThemePack(activeThemePack) || forceSaveAsNew);
      const others = allThemes?.filter((theme) => theme.color_pack !== activeThemePack.color_pack);
      const exists = others?.some(
        (theme) => theme.title.trim().toLocaleLowerCase() === activeThemePack.title.trim().toLocaleLowerCase()
      );
      setNameError(exists ? 'Please pick a unique name' : '');
    }
  }, [open, activeThemePack, allThemes, forceSaveAsNew]);

  useEffect(() => {
    const taskScheduler = createScheduler();
    taskScheduler.schedule(
      () => {
        const others = allThemes?.filter((theme) => theme.color_pack !== activeThemePack.color_pack);
        const exists = others?.some(
          (theme) => theme.title.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase()
        );
        setNameError(exists ? 'Please pick a unique name' : '');
      },
      {
        delay: 0,
        priority: 'user-visible',
      }
    );

    return () => {
      taskScheduler?.yield?.();
    };
  }, [activeThemePack.color_pack, allThemes, name]);

  if (!currentColors) return null;
  return (
    <Modal
      open={open}
      contentWrapperClassName={styles['modal-body']}
      modalClassName={styles.modal}
      customSize={{
        height: '320px',
      }}
      RenderHeaderContent={() => (
        <Container padding="20px 40px 20px 40px" background="transparent">
          <h2>Save theme pack</h2>
        </Container>
      )}
      RenderFooterContent={() => (
        <Container padding="0 40px 20px 40px" className="flex jc-flex-end gap-8 height-64" background="transparent">
          <div
            aria-disabled={editingNonSelectedTheme}
            className={`flex flex-1 gap-8 ai-center ${editingNonSelectedTheme ? 'disabled' : ''}`}
          >
            <label className="P1">Save as new?</label>
            <Checkbox
              value={'save as new'}
              onChange={(_, isOn) => setSaveAsNew(isOn)}
              checked={saveAsNew || lockedToNew}
              disabled={lockedToNew}
            />
          </div>
          <div className="flex gap-8">
            <Button
              onClick={close}
              backgroundColor="var(--gray-650)"
              hoverBackgroundColor="var(--gray-600)"
              className="L1"
            >
              Cancel
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              onClick={() => onSave(name, saveAsNew)}
              disabledOpacity={0.5}
              className={'flex ai-center jc-center'}
              disabled={saving || !!nameError}
              loading={saving}
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1"
                whiteSpace="nowrap"
              >
                Save
              </Text>
            </Button>
          </div>
        </Container>
      )}
    >
      <Container background="transparent" className="flex flex-column gap-36" padding="0 40px">
        <InputWithOverlay
          label="Name*"
          inputProps={{
            value: name,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
            },
          }}
          status={nameError ? 'error' : undefined}
        />
        <Container
          borderRadius="100px"
          padding="20px"
          background="transparent"
          borderColor="var(--gray-200)"
          borderWidth="1px"
          className="flex ai-center width-100-percent height-48 position-relative"
        >
          <Text
            tag={'label'}
            className="L2"
            style={{ position: 'absolute', top: -18, marginLeft: -2, color: 'var(--gray-600)' }}
          >
            Colors
          </Text>
          <div className="flex width-100-percent" style={{ height: 16, borderRadius: '100px', overflow: 'hidden' }}>
            {COLOR_VARIABLES_LIST_V2.map(({ name }) => {
              const color = currentColors[name];
              if (!color) return null;
              const [hex] = color;
              return <div key={name} data-color={name} style={{ height: 16, flex: 1, backgroundColor: hex }} />;
            })}
          </div>
        </Container>
      </Container>
    </Modal>
  );
}
